import moment from "moment";
import forEach from "lodash/forEach";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import settings from "../settings";
import { t } from "./localization";

const encyclopedia = window.__PRELOADED_STATE__ && window.__PRELOADED_STATE__.encyclopedia || {};

export const BUILDING_TYPES = Object.freeze({
  NAVY_YARD: 'navy_yard',
  DRY_DOCK: 'dry_dock',
  ENGINEERING: 'engineering',
  BARRACKS: 'barracks',
  MONUMENT: 'monument'
});

export const BUILDING_TYPES_LIST = Object.values(BUILDING_TYPES);

let shipGlossMap = {};
if (encyclopedia.vehiclesTypes) {
  for (const key in encyclopedia.vehiclesTypes) {
    const vehiclesType = encyclopedia.vehiclesTypes[key];
    shipGlossMap[key] = vehiclesType.mark;
  }
}

export const SHIP_CLASSES = shipGlossMap;

let nationGlossMap = {};
if (encyclopedia.nations) {
  for (const key in encyclopedia.nations) {
    const nation = encyclopedia.nations[key];
    nationGlossMap[key] = nation.mark;
  }
}

export const NATIONS = nationGlossMap;

export const NATIONS_LIST = Object.keys(NATIONS);

const blocksToModifierIds = { // handcrafted from glossary
  [BUILDING_TYPES.BARRACKS]: {
    0: 4276738992, // cost: 0 built by default
    1: 4278836144, // cost: 100000
    2: 4277787568 // cost: 500000
  },
  [BUILDING_TYPES.ENGINEERING]: {
    1: 4279884720, // ship_nation: japan
    2: 4281981872, // ship_nation: germany
    3: 4285127600, // ship_nation: france,
    4: 4283030448, // ship_nation: ussr
    5: 4280933296, // ship_nation: usa
    6: 4284079024, // ship_nation: uk
    7: 4274641840 // ship_nation: pan_asia
  },
  [BUILDING_TYPES.DRY_DOCK]: {
    1: 4289321904, // ship_class: Destroyer
    2: 4288273328, // ship_class: Cruiser
    3: 4287224752, // ship_class: Battleship
    4: 4286176176 // ship_class: AirCarrier
  },
  [BUILDING_TYPES.NAVY_YARD]: {
    1: 4290370480, // ship_level: 8,
    2: 4293516208, // ship_level: 5
    3: 4291419056, // ship_level: 7,
    4: 4292467632 // ship_level: 6
  },
  [BUILDING_TYPES.MONUMENT]: {
    1: 4275690416
  }
};


const modifierIdsToBlocks = {};
forEach(blocksToModifierIds, mapping => {
  forEach(mapping, (modifierId, block) => {
    modifierIdsToBlocks[modifierId] = +block;
  });
});

export const BARRACKS_SQUADRON_MODIFIRE = 4278836144;

export const getMaxLevel = () => Math.max.apply(null, Object.keys(settings.supply.clanLevelingToResource));

export const getClassClanLeverWrapper = clanLevel => {
  let classClanLevelWrapper = null;

  if (clanLevel > 11) {
    classClanLevelWrapper = 3;
  } else if (clanLevel > 8) {
    classClanLevelWrapper = 2;
  } else if (clanLevel > 4) {
    classClanLevelWrapper = 1;
  } else if (clanLevel > 0) {
    classClanLevelWrapper = 0;
  }

  return classClanLevelWrapper;
};

export const modifierToBlock = modifier => modifierIdsToBlocks[modifier.id];

export const modifierGetById = modifierId => settings.supply.modifierTypes[modifierId];

export const buildingBlocks = (building, hasPermission = false, resource = 0) => {
  const allModifiers = settings.supply.buildingTypes[building.name].modifiers.map(modifierGetById);
  return allModifiers.map(modifier => {
    const isActivated = buildingHasModifier(building, modifier);
    return {
      id: modifierToBlock(modifier),
      isActivated,
      canActivate: !isActivated && hasPermission && modifierCanActivate(modifier, resource)
    };
  });
};

export const buildingTypeBlockToModifier = (buildingType, block) => modifierGetById(blocksToModifierIds[buildingType][block]);

export const buildingTypeTitle = buildingType => settings.supply.buildingTypes[buildingType].title;

export const buildingTypeGetOnlyModifier = buildingType => modifierGetById(settings.supply.buildingTypes[buildingType].modifiers[0]);

export const buildingTypeModifierValue = buildingType => settings.supply.buildingTypes[buildingType].modifier_value;

export const modifierCanActivate = (modifier, resource) => modifier.cost <= resource;

export const buildingHasModifier = (building, modifier) => building.modifiers.some(modifierId => modifierId === modifier.id);

export const isAllBonusesActivated = (base) => {
  if (isEmpty(base)) return false;

  const isDiff = Object.values(settings.supply.buildingTypes).some((buildingType: any) => {
    return (!base[buildingType.name] || base[buildingType.name].modifiers.length !== buildingType.modifiers.length);
  });
  return !isDiff;
};

export const countActivatedBlocks = (blocks: any) => {
  let activatedBlocksCount = 0;
  blocks.forEach(({
    isActivated
  }) => {
    if (isActivated) {
      activatedBlocksCount += 1;
    }
  });
  return activatedBlocksCount;
};

export const buildingTypeGetModifiers = (buildingType: string) => settings.supply.buildingTypes[buildingType].modifiers.map(modifierGetById);

export const maxMembersCount = () => settings.clanMembersLimit + maxExtraMembersCount();

export const maxExtraMembersCount = () => {
  return 0;
};

export const needShowIntroduction = (state: any) => {
  const isSupplyEnabled = settings.supply.isEnabled;
  const currentAccount = state.currentAccount;
  const isShown = state.introduction.isShown;
  const releaseDate = settings.supply.releaseDate;
  return (isSupplyEnabled && !isShown && !!currentAccount.clanId && moment(currentAccount.joinedAtClan).isBefore(releaseDate));
};

export const getClanBaseByClanId = (state: any, clanId: string) => get(state.supply, [clanId, 'base'], null);